import { useState, useEffect, type ComponentType, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  TrendingUp,
  TrendingDown,
  Wallet,
  CreditCard,
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Calendar,
  AlertCircle,
  Clock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AmountText,
  FinanceStatusBadge,
  TransactionTypeIcon,
  toneForTransactionType,
  type AmountTone
} from '@/components/finance-v2/FinanceBadges';
import { financeV2Service } from '../services/financeV2Service';
import type { Account, Transaction } from '../types/financeV2.types';
import { parseDbTimestamp } from '../utils/datetime';

import { getErrorMessage } from '../utils/error.utils'

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const formatDate = (date: string) => parseDbTimestamp(date)?.toLocaleDateString('pt-BR') ?? '-';

const balanceTone = (value: number): AmountTone => (value >= 0 ? 'income' : 'expense');

interface KpiCardProps {
  label: string;
  value: ReactNode;
  icon: ComponentType<{ className?: string }>;
  trailing?: ReactNode;
  footer: ReactNode;
}

function KpiCard({ label, value, icon: Icon, trailing, footer }: KpiCardProps) {
  return (
    <Card size="sm">
      <CardContent className="flex h-full flex-col gap-2">
        <div className="flex items-center gap-3">
          <Icon className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
          <div className="min-w-0 flex-1">
            <h3 className="truncate text-xs text-muted-foreground">{label}</h3>
            {value}
          </div>
          {trailing}
        </div>
        <p className="text-xs text-muted-foreground">{footer}</p>
      </CardContent>
    </Card>
  );
}

interface QuickLinkProps {
  to: string;
  title: string;
  description: string;
  icon: ComponentType<{ className?: string }>;
}

function QuickLink({ to, title, description, icon: Icon }: QuickLinkProps) {
  return (
    <Link
      to={to}
      className="flex min-w-0 items-center gap-3 rounded-xl bg-card p-4 ring-1 ring-foreground/10 transition-colors hover:bg-muted"
    >
      <Icon className="size-6 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0">
        <h3 className="font-medium">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
    </Link>
  );
}

function EmptyList({ text, linkTo, linkLabel }: { text: string; linkTo: string; linkLabel: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <p className="text-sm text-muted-foreground">{text}</p>
      <Button variant="outline" size="sm" asChild>
        <Link to={linkTo}>{linkLabel}</Link>
      </Button>
    </div>
  );
}

export function FinanceV2DashboardPage() {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [accountsRes, transactionsRes] = await Promise.all([
        financeV2Service.accounts.list({ page: 1, limit: 100 }),
        financeV2Service.transactions.list({ page: 1, limit: 20 })
      ]);

      setAccounts(accountsRes.data.accounts);
      setTransactions(transactionsRes.data.transactions);
    } catch (err: unknown) {
      setError(getErrorMessage(err, 'Erro ao carregar dados do dashboard'));
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const totalBalance = accounts.reduce((sum, acc) => sum + (acc.currentBalance || 0), 0);
  const activeAccounts = accounts.filter(acc => acc.isActive).length;

  const pendingTransactions = transactions.filter(t => t.status === 'PENDING');
  const paidTransactions = transactions.filter(t => t.status === 'PAID');
  const today = new Date().toISOString().split('T')[0];
  const overdueTransactions = transactions.filter(t => {
    if (t.status !== 'PENDING') return false;
    return t.dueDate.slice(0, 10) < today;
  });

  const totalPending = pendingTransactions.reduce((sum, t) => {
    const amount = t.amount || 0;
    if (t.transactionType === 'INCOME') return sum + amount;
    if (t.transactionType === 'EXPENSE') return sum - amount;
    return sum;
  }, 0);

  const totalIncome = transactions
    .filter(t => t.transactionType === 'INCOME' && t.status === 'PAID')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  const totalExpense = transactions
    .filter(t => t.transactionType === 'EXPENSE' && t.status === 'PAID')
    .reduce((sum, t) => sum + (t.amount || 0), 0);

  if (loading) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6" aria-busy="true">
        <span className="sr-only">Carregando dashboard...</span>
        <Skeleton className="h-8 w-64 max-w-full" />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-4 p-4 sm:p-6">
        <div role="alert" className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive">
          {error}
        </div>
      </div>
    );
  }

  const paidIncomeCount = paidTransactions.filter(t => t.transactionType === 'INCOME').length;
  const paidExpenseCount = paidTransactions.filter(t => t.transactionType === 'EXPENSE').length;

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex items-center gap-2">
          <DollarSign className="size-6 shrink-0 text-muted-foreground" />
          <h1 className="min-w-0 break-words text-2xl font-semibold">Dashboard Financeiro V2</h1>
        </div>
        <p className="text-sm text-muted-foreground">Visão geral das suas finanças</p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Saldo Total"
          icon={Wallet}
          value={<AmountText tone={balanceTone(totalBalance)} className="text-lg font-semibold">{formatCurrency(totalBalance)}</AmountText>}
          trailing={totalBalance >= 0
            ? <TrendingUp className="size-5 shrink-0 text-primary" aria-hidden="true" />
            : <TrendingDown className="size-5 shrink-0 text-destructive" aria-hidden="true" />}
          footer={`${activeAccounts} conta${activeAccounts !== 1 ? 's' : ''} ativa${activeAccounts !== 1 ? 's' : ''}`}
        />
        <KpiCard
          label="Receitas Pagas"
          icon={ArrowUpRight}
          value={<AmountText tone="income" className="text-lg font-semibold">{formatCurrency(totalIncome)}</AmountText>}
          footer={`${paidIncomeCount} transações`}
        />
        <KpiCard
          label="Despesas Pagas"
          icon={ArrowDownRight}
          value={<AmountText tone="expense" className="text-lg font-semibold">{formatCurrency(totalExpense)}</AmountText>}
          footer={`${paidExpenseCount} transações`}
        />
        <KpiCard
          label="A Pagar/Receber"
          icon={Clock}
          value={
            <AmountText tone={totalPending >= 0 ? 'neutral' : 'expense'} className="text-lg font-semibold">
              {formatCurrency(Math.abs(totalPending))}
            </AmountText>
          }
          footer={`${pendingTransactions.length} pendente${pendingTransactions.length !== 1 ? 's' : ''}`}
        />
      </div>

      {overdueTransactions.length > 0 && (
        <div role="alert" className="flex items-start gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden="true" />
          <div className="min-w-0">
            <h3 className="font-medium text-destructive">Transações Vencidas</h3>
            <p className="text-muted-foreground">
              Você tem {overdueTransactions.length} transaç{overdueTransactions.length !== 1 ? 'ões' : 'ão'} vencida{overdueTransactions.length !== 1 ? 's' : ''}.
              <Link to="/finance-v2/transactions" className="ml-2 text-foreground underline underline-offset-4">
                Ver transações
              </Link>
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="min-w-0">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle><h2>Transações Recentes</h2></CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/finance-v2/transactions">Ver todas</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <EmptyList text="Nenhuma transação registrada" linkTo="/finance-v2/transactions" linkLabel="Criar primeira transação" />
            ) : (
              <ul className="flex flex-col divide-y">
                {transactions.slice(0, 10).map((transaction) => {
                  const tone = toneForTransactionType(transaction.transactionType);
                  const isTransfer = tone === 'neutral';
                  return (
                    <li
                      key={transaction.id}
                      className="flex flex-col gap-2 py-3 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-3">
                        {isTransfer
                          ? <CreditCard className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                          : <TransactionTypeIcon type={transaction.transactionType} />}
                        <div className="min-w-0">
                          <p className="wrap-break-word text-sm font-medium">{transaction.description}</p>
                          <div className="mt-1 flex flex-wrap items-center gap-2">
                            <span className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Calendar className="size-3" aria-hidden="true" />
                              {formatDate(transaction.dueDate)}
                            </span>
                            {transaction.status === 'PAID' ? (
                              <FinanceStatusBadge status="Pago" />
                            ) : transaction.dueDate.slice(0, 10) < today ? (
                              <FinanceStatusBadge status="Vencido" />
                            ) : (
                              <FinanceStatusBadge status="Pendente" />
                            )}
                          </div>
                        </div>
                      </div>
                      <AmountText tone={tone} className="pl-8 text-sm font-semibold sm:pl-0">
                        {tone === 'income' ? '+' : tone === 'expense' ? '-' : ''}
                        {formatCurrency(transaction.amount || 0)}
                      </AmountText>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card className="min-w-0">
          <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle><h2>Contas</h2></CardTitle>
            <Button variant="outline" size="sm" asChild>
              <Link to="/finance-v2/accounts">Ver todas</Link>
            </Button>
          </CardHeader>
          <CardContent>
            {accounts.length === 0 ? (
              <EmptyList text="Nenhuma conta cadastrada" linkTo="/finance-v2/accounts" linkLabel="Criar primeira conta" />
            ) : (
              <ul className="flex flex-col gap-4">
                {accounts.slice(0, 6).map((account) => (
                  <li key={account.id} className="flex items-center justify-between gap-3">
                    <div className="flex min-w-0 items-center gap-3">
                      <Wallet className="size-5 shrink-0 text-muted-foreground" aria-hidden="true" />
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">{account.name}</p>
                        <p className="text-xs text-muted-foreground">{account.type}</p>
                      </div>
                    </div>
                    <div className="shrink-0 text-right">
                      <AmountText tone={balanceTone(account.currentBalance || 0)} className="text-sm font-semibold">
                        {formatCurrency(account.currentBalance || 0)}
                      </AmountText>
                      {!account.isActive && (
                        <p className="text-xs text-muted-foreground">Inativa</p>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <QuickLink to="/finance-v2/transactions" icon={CreditCard} title="Nova Transação" description="Registrar receita ou despesa" />
        <QuickLink to="/finance-v2/transfers" icon={ArrowUpRight} title="Nova Transferência" description="Transferir entre contas" />
        <QuickLink to="/finance-v2/accounts" icon={Wallet} title="Nova Conta" description="Cadastrar conta bancária" />
      </div>
    </div>
  );
}
